import _ from 'underscore';
import { useContext, useMemo, useCallback, useEffect, useState } from 'react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import type { Flight, ServerContextType, UseFlightsReturn, UseFlightsParams, FlightStatus } from './index.d';
import {
  FLIGHT_STATUS,
} from './constants';
import { getCustomerFlightAllocations } from './fetch-flights';
import { isFlightMatch, getFlightUrlOverrideStatus } from './utils';
import { createServerContextFlightParser } from './parse-flights-from-context';
import { useFlightInstrumentation } from './use-flight-instrumentation';

const getFlightsFromServerContext = createServerContextFlightParser('CustomerFlightAllocations');

export type UseCustomerFlightsParams = UseFlightsParams & {
  /** Optional: When provided, fetches flight allocations via OData instead of reading from ServerContext */
  customerId?: number;
};

// @aiContributed-MAPFuse-2026-04-27
const useCustomerFlights = ({
  treatmentFlightId,
  controlFlightId,
  treatmentFlightName,
  controlFlightName,
  autoLog = true,
  customerId,
}: UseCustomerFlightsParams): UseFlightsReturn => {
  const [shouldLog, setShouldLog] = useState(autoLog);
  const serverContext = useContext<ServerContextType>(ServerContext);

  const oDataToken = serverContext?.Token || '';
  const campaignApiBase = serverContext?.ServicesEndpoints?.CampaignAPI;

  const [experimentFlight, setExperimentFlight] = useState<Flight | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  const { flightActivity, nonFlightActivity, allFlightActivity } = useFlightInstrumentation('CustomerFlights');

  const getFlightsPromise = useMemo(() => {
    const serverContextCustomerId = serverContext?.CurrentAccount?.AdvertiserCustomerID;
    const isEmptyAllocations = !serverContext?.CustomerFlightAllocations || serverContext?.CustomerFlightAllocations === '[]';

    const resolvedCustomerId = customerId ?? serverContextCustomerId;

    let shouldGetFromServerContext = true;
    if (customerId) {
      // Use ServerContext if IDs match and allocations are non-empty
      if (!(customerId === serverContextCustomerId && !isEmptyAllocations)) {
        shouldGetFromServerContext = false;
      }
    } else if (isEmptyAllocations && resolvedCustomerId) {
      // ServerContext allocations are empty but we have a valid ID to fetch via OData
      shouldGetFromServerContext = false;
    }

    if (shouldGetFromServerContext) {
      return Promise.resolve(getFlightsFromServerContext({ serverContext, allFlightActivity }) ?? []);
    }

    return getCustomerFlightAllocations({
      cid: resolvedCustomerId!,
      campaignApiBase,
      oDataToken,
      activity: allFlightActivity,
    });
  }, [customerId, serverContext, campaignApiBase, oDataToken, allFlightActivity]);

  useEffect(() => {
    getFlightsPromise
      .then((flights: Flight[]) => {
        const flight = _.find(flights, (f: Flight): boolean =>
          isFlightMatch(f, treatmentFlightId, treatmentFlightName) ||
          isFlightMatch(f, controlFlightId, controlFlightName));
        setExperimentFlight(flight);
        setIsLoading(false);
      })
      .catch((error) => {
        allFlightActivity.trace(JSON.stringify({ error: error?.message }));
        setIsLoading(false);
      });
  }, [allFlightActivity, controlFlightId, treatmentFlightId, treatmentFlightName, controlFlightName, getFlightsPromise]);

  const log = useCallback(() => {
    if (experimentFlight) {
      flightActivity.trace(JSON.stringify(experimentFlight));
    } else {
      nonFlightActivity.trace(JSON.stringify({ treatmentFlightId, controlFlightId }));
    }
  }, [flightActivity, nonFlightActivity, treatmentFlightId, controlFlightId, experimentFlight]);

  const urlOverride = useMemo(
    () => getFlightUrlOverrideStatus(treatmentFlightId, treatmentFlightName),
    [treatmentFlightId, treatmentFlightName]
  );

  const flightStatus = useMemo((): FlightStatus => {
    if (isLoading) {
      return FLIGHT_STATUS.LOADING;
    }

    // support flight url override (e.g. ?flights=123,myFlightName)
    if (urlOverride !== null) {
      return urlOverride;
    }

    if (isFlightMatch(experimentFlight, treatmentFlightId, treatmentFlightName)) {
      return FLIGHT_STATUS.IN_TREATMENT;
    } else if (isFlightMatch(experimentFlight, controlFlightId, controlFlightName)) {
      return FLIGHT_STATUS.IN_CONTROL;
    }

    return FLIGHT_STATUS.NOT_IN_FLIGHT;
  }, [isLoading, experimentFlight, urlOverride, treatmentFlightId, controlFlightId, treatmentFlightName, controlFlightName]);

  const logFlight = useCallback(() => {
    setShouldLog(true);
  }, []);

  useEffect(() => {
    if (shouldLog && urlOverride === null && flightStatus !== FLIGHT_STATUS.LOADING) {
      log();
    }
  }, [shouldLog, urlOverride, flightStatus, log]);

  return {
    flightStatus,
    cohortIndex: experimentFlight?.CohortIndex ?? undefined,
    rampAllocationId: experimentFlight?.RampAllocationId ?? undefined,
    ...(autoLog ? {} : { logFlight }),
  };
};

export { useCustomerFlights, getFlightsFromServerContext };
